import { PropTypes } from "prop-types";
import React, { Component } from "react";
import { connect } from "react-redux";
import Axios from "axios";
import Loading from "../Admin/Components/Loading";
import { sanitizeRichHtml } from "../../sanitizeHtml";
import ViewContentWindow from "./ViewContentWindow";

const axios = Axios.create();

// React-Client#119 / dbServer#13: the forced-acknowledgement content type.
// Shows customizable text above and below a single hosted (embedded) content
// item, plus a click-to-acknowledge control. On confirm it POSTs to
// /content/:id/acknowledge so the per-user first-saw/last-saw/confirmed audit is
// recorded. The parent (Help Center) decides whether to keep showing it
// (superplay) until confirmed via GET /content/acknowledgements/mine.
class AcknowledgementView extends Component {
  constructor(props) {
    super(props);
    this.state = {
      embedded: false,
      loadingEmbedded: !!props.content.EmbeddedContentId,
      checked: false,
      confirming: false,
      confirmed: false,
      // #279: confirmed BEFORE this session -> suppress entirely (never re-show
      // or re-record 'seen'). Distinct from `confirmed` (just did it -> thanks).
      alreadyConfirmed: !!props.confirmed,
      error: false,
    };
  }

  componentDidMount() {
    // Preview mode (admin layout preview): render the ack faithfully but with
    // ZERO side effects. Fetch the embedded content only to DISPLAY it (a
    // read-only GET); never record a 'seen', never confirm, never beacon, so a
    // preview cannot pollute analytics or re-create acknowledgement rows.
    if (this.props.preview) {
      if (this.props.content.EmbeddedContentId) {
        this.fetchEmbedded();
      }
      return;
    }
    // #279: a confirmed acknowledgement must never re-show or re-record 'seen'.
    if (this.props.confirmed !== undefined) {
      // Parent supplied the confirmed state (HelpCenter). Trust it.
      if (!this.props.confirmed) {
        this.seeAndFetch();
      }
      return;
    }
    // Record-page paths (ContentCard/ViewContentWindow) don't pass it, so check
    // the user's confirmed set ourselves before showing / recording.
    this.checkConfirmedThenSee();
  }

  seeAndFetch() {
    this.recordSeen();
    if (this.props.content.EmbeddedContentId) {
      this.fetchEmbedded();
    }
  }

  // #279: don't show or record a 'seen' for an ack this user already confirmed.
  checkConfirmedThenSee() {
    axios
      .get(
        "https://" +
          process.env.REACT_APP_API +
          "/content/acknowledgements/mine",
        { headers: { jwttoken: this.token() } }
      )
      .then((success) => {
        const isConfirmed = (success.data || []).some(
          (a) =>
            (a.Confirmed || a.ConfirmedDate) &&
            String(a.ContentId) === String(this.props.content.ContentId)
        );
        if (isConfirmed) {
          this.setState({ alreadyConfirmed: true });
        } else {
          this.seeAndFetch();
        }
      })
      .catch(() => {
        // If we can't check, fail toward SHOWING the ack (compliance) + record.
        this.seeAndFetch();
      });
  }

  token() {
    return this.props.user && this.props.user.userData
      ? this.props.user.userData.token
      : undefined;
  }

  // Fetch the hosted content so it can be rendered by the shared viewer.
  fetchEmbedded() {
    axios
      .get(
        "https://" +
          process.env.REACT_APP_API +
          "/content/" +
          this.props.content.EmbeddedContentId,
        { headers: { jwttoken: this.token() } }
      )
      .then((success) => {
        const embedded =
          success.data && success.data.length ? success.data[0] : false;
        this.setState({ embedded: embedded, loadingEmbedded: false });
        // #279: a forced ack whose content is gone must fail LOUD (compliance).
        if (!embedded) this.beaconDeliveryFailure("embedded-content-missing");
      })
      .catch(() => {
        this.setState({ loadingEmbedded: false, error: "embedded" });
        this.beaconDeliveryFailure("embedded-fetch-error");
      });
  }

  // #279: tell the server a forced ack could not be delivered so it fails LOUD
  // (CloudWatch metric -> alarm -> Lynceon triage). Best-effort; never blocks.
  beaconDeliveryFailure(reason) {
    if (this.props.preview) return; // a preview never beacons
    axios
      .post(
        "https://" +
          process.env.REACT_APP_API +
          "/content/" +
          this.props.content.ContentId +
          "/ack-delivery-failure",
        { reason: reason },
        { headers: { jwttoken: this.token() } }
      )
      .catch(() => {});
  }

  // Record that the user saw this acknowledgement (confirm=0), for the audit.
  recordSeen() {
    if (this.state.confirmed) return;
    axios
      .post(
        "https://" +
          process.env.REACT_APP_API +
          "/content/" +
          this.props.content.ContentId +
          "/acknowledge",
        { confirm: 0 },
        { headers: { jwttoken: this.token() } }
      )
      .catch(() => {
        // a failed "seen" record is non-fatal; the confirm below is what matters
      });
  }

  confirm() {
    if (this.props.preview) return; // a preview never records a confirmation
    if (!this.state.checked || this.state.confirming) return;
    this.setState({ confirming: true });
    axios
      .post(
        "https://" +
          process.env.REACT_APP_API +
          "/content/" +
          this.props.content.ContentId +
          "/acknowledge",
        { confirm: 1 },
        { headers: { jwttoken: this.token() } }
      )
      .then(() => {
        this.setState({ confirming: false, confirmed: true });
        if (this.props.onConfirmed) {
          this.props.onConfirmed(this.props.content.ContentId);
        }
      })
      .catch(() => {
        this.setState({ confirming: false, error: "confirm" });
      });
  }

  renderEmbedded() {
    if (!this.props.content.EmbeddedContentId) return null;
    if (this.state.loadingEmbedded) return <Loading />;
    if (!this.state.embedded) {
      return (
        <p className="text-muted">This help content is no longer available.</p>
      );
    }
    return (
      <div
        style={{
          border: "1px solid #e0e0e0",
          borderRadius: "4px",
          padding: "8px",
          margin: "12px 0",
        }}
      >
        <ViewContentWindow content={this.state.embedded} embedded={true} />
      </div>
    );
  }

  render() {
    const c = this.props.content;
    // #279: an ack this user already confirmed is suppressed entirely.
    if (this.state.alreadyConfirmed) {
      return null;
    }
    if (this.state.confirmed) {
      return (
        <div className="p-4 text-center">
          <span className="s1icon s1icon-s-task text-success" />
          <h3 className="mt-2">Thank you. Your acknowledgement was recorded.</h3>
        </div>
      );
    }
    return (
      <div className="p-3" style={{ maxWidth: "800px", margin: "0 auto" }}>
        {c.ContentLabel ? <h2>{c.ContentLabel}</h2> : null}

        {c.AboveText ? (
          <div
            className="ql-editor"
            dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(c.AboveText) }}
          />
        ) : null}

        {this.renderEmbedded()}

        <div className="card mt-3" style={{ padding: "12px" }}>
          {/* #397: the "text shown below the content" is the agreement label
              next to the confirm checkbox. */}
          <label
            style={{
              cursor: "pointer",
              display: "flex",
              alignItems: "flex-start",
              gap: "8px",
            }}
          >
            <input
              type="checkbox"
              checked={this.state.checked}
              onChange={(e) => this.setState({ checked: e.target.checked })}
              style={{ marginTop: "4px" }}
            />
            {c.BelowText ? (
              <span
                className="ql-editor"
                style={{ padding: 0 }}
                dangerouslySetInnerHTML={{
                  __html: sanitizeRichHtml(c.BelowText),
                }}
              />
            ) : (
              <span>I have read and understood the above.</span>
            )}
          </label>
          <div className="mt-2">
            <button
              type="button"
              className="btn btn-primary"
              disabled={
                this.props.preview ||
                !this.state.checked ||
                this.state.confirming
              }
              onClick={this.confirm.bind(this)}
            >
              {this.state.confirming ? "Recording..." : "Confirm"}
            </button>
            {this.props.preview ? (
              <small className="text-muted ml-2">
                Preview: confirming is disabled.
              </small>
            ) : null}
            {/* #279: Defer/Skip appears only when the ack allows deferral AND
                the host (autoplay layer) provides a defer handler. */}
            {c.AllowDefer && this.props.onDefer ? (
              <button
                type="button"
                className="btn btn-default ml-2"
                disabled={this.state.confirming}
                onClick={() => this.props.onDefer(c.ContentId)}
              >
                Not now
              </button>
            ) : null}
          </div>
          {this.state.error === "confirm" ? (
            <p className="text-danger mt-2">
              Something went wrong recording your acknowledgement. Please try
              again.
            </p>
          ) : null}
        </div>
      </div>
    );
  }
}

AcknowledgementView.propTypes = {
  content: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  onConfirmed: PropTypes.func,
  confirmed: PropTypes.bool,
  onDefer: PropTypes.func,
  preview: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  user: state.user,
});

export default connect(mapStateToProps, {})(AcknowledgementView);
